import { useState } from 'react';
import { fieldMessage } from '@app/errors';
import { Alert, Card, ErrorBanner, FieldGrid, LoadingButton } from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { useUpdateInfraGroup } from './Infra.api';
import {
  fieldsAlterados,
  asList,
  listForText,
  groupSummary,
  valuesIniciais,
  type InfraGroup,
  type ValueField,
  type ValuesGroup,
  groupDescription,
  groupLabel,
  groupWarning,
  visibleFields,
} from './InfraModel';
import { BadgeReadiness, InfraField } from './InfraField';
import { InfraTestCard } from './InfraTestCard';
import { t, tn } from '@app/i18n';
import s from './Infra.module.css';

export interface InfraGroupTabProps {
  grupo: InfraGroup;
  /**
   * O site que recebe a ordem (S-e (2)). Ausente = o padrão, que é o caminho de
   * toda instalação single-site.
   *
   * Vem por prop, e não de um hook aqui dentro, porque quem escolhe é a página:
   * dois componentes lendo o seletor por conta própria poderiam discordar entre
   * o que a tela mostra e o cluster que recebe o `PUT`.
   */
  siteId?: string;
}

/**
 * Uma aba de T20: os campos do grupo, o **Salvar grupo** e o card Conexão.
 *
 * O formulário é `useState` puro em vez de react-hook-form porque os campos não
 * são conhecidos em tempo de compilação — eles vêm do middleware (FR-018), e o
 * que a tela precisa saber é só o que **mudou** em relação ao valor efetivo.
 */
export function InfraGroupTab({ grupo, siteId }: InfraGroupTabProps) {
  const canSave = useCanOperar('updateInfraConfigGroup');
  const save = useUpdateInfraGroup();

  const [valores, setValores] = useState<ValuesGroup>(() => valuesIniciais(grupo));
  const [base, setBase] = useState(grupo);

  /**
   * Realinha o formulário quando a configuração é relida (FR-022): depois de
   * salvar, o que vale é o **valor efetivo** que voltou do executor, não o que
   * foi digitado. Ajuste durante o render (padrão do React para estado derivado)
   * em vez de `key` no pai — remontar levaria junto o resultado do salvar.
   */
  if (base !== grupo) {
    setBase(grupo);
    setValores(valuesIniciais(grupo));
  }

  const alterados = fieldsAlterados(grupo, valores);
  const count = Object.keys(alterados).length;
  const { pronto, faltam } = groupSummary(grupo);

  /**
   * «Salvar» só aparece quando o grupo tem o que gravar. O executor real publica abas
   * inteiras que o `PUT` recusa (`coletor`, `agentes`: o deploy decide), e ali o botão
   * oferecia uma gravação que nunca acontece.
   */
  // Os campos que a tela mostra (sem a marca `hidden`, aim-o3r.14). O «Salvar» conta só
  // estes: um campo escondido e gravável não pode manter um botão que não grava nada à vista.
  const campos = visibleFields(grupo);
  const hasEditable = campos.some((c) => c.editable);

  /** Erros por campo do 422 (FR-127): `campos[].campo` casa com o `campo` daqui. */
  const errorsByField = new Map((save.error?.campos ?? []).map((e) => [e.field, fieldMessage(e)]));

  /**
   * `aplicado[]` do `PUT`. Passa por `comoLista` pela mesma razão que o `valor` de
   * campo de lista: é uma lista publicada pelo executor, e uma lista que chega
   * como texto não pode derrubar a tela **depois** de a gravação ter dado certo.
   */
  const aplicado = asList(save.data?.applied);

  function define(campo: string, valor: ValueField) {
    setValores((current) => ({ ...current, [campo]: valor }));
  }

  function submeter(e: React.FormEvent) {
    e.preventDefault();
    save.mutate(
      { grupo: grupo.name, fields: alterados, siteId },
      {
        // FR-112/FR-020: o segredo digitado é descartado assim que o PUT passa —
        // não fica no campo (nem no DOM) esperando o realinhamento do grupo. O
        // executor nunca o devolve, então o campo volta a "definido — deixe em
        // branco para manter". (Achado do E2E 2.5, US2.)
        onSuccess: () =>
          setValores((current) => ({
            ...current,
            ...Object.fromEntries(
              grupo.fields.filter((c) => c.sensitive).map((c) => [c.field, '']),
            ),
          })),
      },
    );
  }

  const aviso = groupWarning(grupo);

  return (
    <div className={s.aba}>
      <Card
        title={groupLabel(grupo)}
        subtitle={groupDescription(grupo)}
        toolbar={<BadgeReadiness pronto={pronto} faltam={faltam} />}
      >
        {aviso ? (
          // Aviso fixo da aba, publicado pelo executor (FR-024). `info` de
          // propósito: é texto permanente da tela, não algo que acabou de
          // acontecer — `role="alert"` aqui competiria com o banner de erro de
          // verdade e atrapalharia quem usa leitor de tela.
          <Alert tone="info">{aviso}</Alert>
        ) : null}

        {!pronto && faltam > 0 ? (
          <Alert tone="info" title={tn('infrastructure.missing.title', faltam)}>
            {listForText(grupo.missing)}
          </Alert>
        ) : null}

        {save.error ? (
          <ErrorBanner error={save.error} escrita title={t('infrastructure.saveError')} />
        ) : null}

        {save.isSuccess && save.data?.nothing_to_save ? (
          // FR-023: o executor respondeu "nada a alterar" — sucesso silencioso.
          <Alert tone="info" title={t('infrastructure.nothingToSave.title')}>
            {t('infrastructure.nothingToSave.body')}
          </Alert>
        ) : null}

        {save.isSuccess && !save.data?.nothing_to_save ? (
          <Alert tone="info" title={t('infrastructure.saved.title')}>
            {aplicado.length
              ? t('infrastructure.saved.applied', { fields: aplicado.join(', ') })
              : t('infrastructure.saved.accepted')}
          </Alert>
        ) : null}

        <form onSubmit={submeter} noValidate>
          <FieldGrid>
            {campos.map((campo) => (
              <InfraField
                key={campo.name}
                campo={campo}
                valor={valores[campo.field] ?? ''}
                onChange={(valor) => define(campo.field, valor)}
                editavel={canSave}
                erro={errorsByField.get(campo.field) ?? null}
              />
            ))}
          </FieldGrid>

          {canSave && hasEditable ? (
            <div className={s.acoes}>
              <span className={s.contadorAlterados} aria-live="polite">
                {count === 0
                  ? t('infrastructure.changed.none')
                  : tn('infrastructure.changed.count', count)}
              </span>
              <LoadingButton type="submit" loading={save.isPending}>
                {t('infrastructure.save')}
              </LoadingButton>
            </div>
          ) : null}
        </form>
      </Card>

      <InfraTestCard grupo={grupo} siteId={siteId} />
    </div>
  );
}
